import { mean, median, medianAbsoluteDeviation, percentile } from '../geometry/statistics.js';
import { clamp, expandRect, intersects, type NormalizedRect } from '../types/geometry.js';
import { numberParam } from '../types/checks.js';
import type { CheckRunContext } from '../types/checks.js';
import type { AnalysisBuffer } from '../types/image.js';
import type { AuthenticitySignal } from './index.js';

/**
 * Signals D (face texture) and E (skin smoothing).
 *
 * Both compare the *local texture* of the skin against something that was not
 * retouched: the background for D, and the sharp features at the face border
 * for E. The comparison — not an absolute threshold — is what makes the signals
 * usable on a flat synthetic fixture and on a real portrait alike.
 */

export interface RoiTextureStats {
  valid: boolean;
  /** Number of sampled pixels. */
  count: number;
  meanLuma: number;
  /** Mean local 3×3 standard deviation — the "texture amplitude". */
  meanStd: number;
  /** Mean absolute central gradient (|dx| + |dy|). */
  meanGradient: number;
  /** 95th percentile of the central gradient — "are there sharp edges?". */
  p95Gradient: number;
  /** 99th percentile of the central gradient — the strongest edges present. */
  p99Gradient: number;
  /** Robust per-ROI noise sigma (MAD of the 3×3-median residual, scaled). */
  noiseSigma: number;
}

const EMPTY_STATS: RoiTextureStats = {
  valid: false,
  count: 0,
  meanLuma: 0,
  meanStd: 0,
  meanGradient: 0,
  p95Gradient: 0,
  p99Gradient: 0,
  noiseSigma: 0,
};

const clamp01 = (value: number): number => clamp(value, 0, 1);

/** The skin ROI: the face box shrunk so that the jaw and hair never leak in. */
export const faceRegion = (ctx: CheckRunContext): NormalizedRect | null => {
  const box = ctx.geometry?.faceBox;
  if (!box || box.width <= 0 || box.height <= 0) return null;
  const inset = 0.16;
  const rect: NormalizedRect = {
    x: box.x + box.width * inset,
    y: box.y + box.height * inset,
    width: box.width * (1 - 2 * inset),
    height: box.height * (1 - 2 * inset),
  };
  return rect.width > 0 && rect.height > 0 ? rect : null;
};

/**
 * Picks the background patches of a coarse grid that do not touch the head
 * (hair included) and are therefore genuinely background.
 *
 * A fixed rectangle would fail as soon as the subject is off-centre, so the
 * grid is searched; the patches farthest from the subject centre come first.
 * Several patches are kept (not just the farthest one) because a single patch
 * can contain a foreign object, and the caller takes the median across them.
 */
export const backgroundRegions = (ctx: CheckRunContext): NormalizedRect[] => {
  const subject = ctx.geometry?.headBox ?? ctx.background?.subjectRect;
  if (!subject) return [];
  const expanded = expandRect(subject, 0.04);
  const patch = 0.2;
  const positions = [0.02, 0.4, 0.78];
  const centreX = subject.x + subject.width / 2;
  const centreY = subject.y + subject.height / 2;

  const candidates: Array<{ rect: NormalizedRect; distance: number }> = [];
  for (const y of positions) {
    for (const x of positions) {
      const rect: NormalizedRect = { x, y, width: patch, height: patch };
      if (intersects(rect, expanded)) continue;
      candidates.push({
        rect,
        distance: Math.hypot(x + patch / 2 - centreX, y + patch / 2 - centreY),
      });
    }
  }
  candidates.sort((a, b) => b.distance - a.distance);
  return candidates.slice(0, 3).map((candidate) => candidate.rect);
};

/** Robust background texture, pooled as the median over the background patches. */
export interface BackgroundTextureStats {
  valid: boolean;
  /** Median local texture amplitude across the background patches. */
  meanStd: number;
  /** Median noise sigma across the background patches. */
  noiseSigma: number;
  /** Number of patches that produced a usable measurement. */
  patches: number;
  count: number;
}

export const backgroundTextureStats = (
  buffer: AnalysisBuffer,
  ctx: CheckRunContext,
): BackgroundTextureStats => {
  const stats = backgroundRegions(ctx)
    .map((rect) => roiTextureStats(buffer, rect))
    .filter((entry) => entry.valid);
  if (stats.length === 0) {
    return { valid: false, meanStd: 0, noiseSigma: 0, patches: 0, count: 0 };
  }
  return {
    valid: true,
    meanStd: median(stats.map((entry) => entry.meanStd)),
    noiseSigma: median(stats.map((entry) => entry.noiseSigma)),
    patches: stats.length,
    count: Math.max(...stats.map((entry) => entry.count)),
  };
};

/** Texture / noise statistics inside a normalized rectangle, sampled on a grid. */
export const roiTextureStats = (
  buffer: AnalysisBuffer,
  rect: NormalizedRect,
): RoiTextureStats => {
  const { luma, width, height } = buffer;
  if (width < 4 || height < 4) return EMPTY_STATS;

  const x0 = clamp(Math.floor(rect.x * width), 0, width - 1);
  const x1 = clamp(Math.ceil((rect.x + rect.width) * width), x0 + 1, width);
  const y0 = clamp(Math.floor(rect.y * height), 0, height - 1);
  const y1 = clamp(Math.ceil((rect.y + rect.height) * height), y0 + 1, height);
  const total = (x1 - x0) * (y1 - y0);
  if (total < 25 || x1 - x0 < 3 || y1 - y0 < 3) return EMPTY_STATS;

  // Deterministic stride: the same image always produces the same sample set.
  const step = Math.max(1, Math.floor(Math.sqrt(total / 4096)));
  const neighbourhood = new Array<number>(9);
  const stds: number[] = [];
  const gradients: number[] = [];
  const residuals: number[] = [];
  let lumaSum = 0;

  for (let y = y0 + 1; y < y1 - 1; y += step) {
    for (let x = x0 + 1; x < x1 - 1; x += step) {
      let index = 0;
      let sum = 0;
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          const value = luma[(y + dy) * width + (x + dx)] as number;
          neighbourhood[index] = value;
          index += 1;
          sum += value;
        }
      }
      const localMean = sum / 9;
      let squared = 0;
      for (const value of neighbourhood) {
        const delta = value - localMean;
        squared += delta * delta;
      }
      const centre = luma[y * width + x] as number;
      stds.push(Math.sqrt(squared / 9));
      gradients.push(
        Math.abs((luma[y * width + x + 1] as number) - (luma[y * width + x - 1] as number)) +
          Math.abs((luma[(y + 1) * width + x] as number) - (luma[(y - 1) * width + x] as number)),
      );
      residuals.push(centre - median(neighbourhood));
      lumaSum += centre;
    }
  }

  if (stds.length === 0) return EMPTY_STATS;
  return {
    valid: true,
    count: stds.length,
    meanLuma: lumaSum / stds.length,
    meanStd: mean(stds),
    meanGradient: mean(gradients),
    p95Gradient: percentile(gradients, 0.95),
    p99Gradient: percentile(gradients, 0.99),
    noiseSigma: medianAbsoluteDeviation(residuals) * 1.4826,
  };
};

const unavailable = (id: AuthenticitySignal['id'], note: string): AuthenticitySignal => ({
  id,
  score: 0,
  confidence: 0.02,
  note,
});

/**
 * D — the skin is smoother than its surroundings.
 *
 * `ratio = background texture / skin texture` is the primary term; the absolute
 * term catches the case where the background is only mildly textured but the
 * skin is almost perfectly flat. Both are gated by `valid` ROIs, so a frame too
 * flat to compare reports a near-zero confidence rather than a guess.
 */
export const faceTextureSignal = (ctx: CheckRunContext): AuthenticitySignal => {
  const pixels = ctx.pixels;
  if (!pixels) return unavailable('face_texture', 'Пиксельный буфер недоступен.');
  const skinRect = faceRegion(ctx);
  if (!skinRect) {
    return unavailable(
      'face_texture',
      'Нет области кожи для сравнения текстуры (нужна геометрия лица).',
    );
  }

  const skin = roiTextureStats(pixels, skinRect);
  const surround = backgroundTextureStats(pixels, ctx);
  if (!skin.valid || !surround.valid) {
    return unavailable('face_texture', 'Области лица или фона слишком малы для оценки текстуры.');
  }

  const ratio = surround.meanStd / Math.max(skin.meanStd, 0.6);
  const ratioScore = clamp01((ratio - 1.3) / 1.5);
  const absoluteScore =
    clamp01((1.6 - skin.meanStd) / 1.6) * clamp01((surround.meanStd - 2) / 5);
  const score = clamp01(Math.max(ratioScore, absoluteScore));

  let confidence = 0.3;
  confidence += surround.meanStd >= 1.5 ? 0.3 : -0.1;
  if (skin.count > 400) confidence += 0.1;
  if (score >= 0.35) confidence += 0.05;
  confidence = clamp01(confidence);

  return {
    id: 'face_texture',
    score,
    confidence,
    note:
      score >= 0.35
        ? 'текстура кожи заметно глаже, чем фон'
        : 'текстура кожи сопоставима с фоном',
  };
};

/**
 * E — beauty filtering: very low local contrast in the skin while the face
 * border (jaw, hair, ears) stays sharp.
 *
 * The signal is gated on the skin being *smoother than the background*, because
 * a genuinely flat studio portrait on a flat background must not be reported as
 * retouched.
 */
export const skinSmoothingSignal = (ctx: CheckRunContext): AuthenticitySignal => {
  const pixels = ctx.pixels;
  if (!pixels) return unavailable('skin_smoothing', 'Пиксельный буфер недоступен.');
  const skinRect = faceRegion(ctx);
  const faceBox = ctx.geometry?.faceBox;
  if (!skinRect || !faceBox) {
    return unavailable(
      'skin_smoothing',
      'Нет области кожи и краёв лица для оценки сглаживания (нужна геометрия лица).',
    );
  }

  const skin = roiTextureStats(pixels, skinRect);
  const surround = backgroundTextureStats(pixels, ctx);
  const edgeBand = roiTextureStats(pixels, expandRect(faceBox, 0.06));
  if (!skin.valid || !surround.valid || !edgeBand.valid) {
    return unavailable('skin_smoothing', 'Недостаточно пикселей для оценки локального контраста.');
  }

  const smoothThreshold = numberParam(ctx.rule.params, 'skinSmoothnessThreshold', 2.4);
  const smooth = clamp01((smoothThreshold - skin.meanStd) / Math.max(smoothThreshold, 0.1));
  // p99 rather than p95: the sharp jaw/hair border occupies only a thin ring of
  // the expanded face box, so the top percentile is the honest "are the edges
  // still there?" measure.
  const sharp = clamp01((edgeBand.p99Gradient - 15) / 60);
  const smootherThanBackground =
    surround.meanStd > 1.2 && skin.meanStd < Math.max(0.7 * surround.meanStd, 1);

  const score = smootherThanBackground ? clamp01(smooth * (0.35 + 0.65 * sharp)) : 0;
  const confidence = smootherThanBackground
    ? clamp01(0.4 + (skin.count > 400 ? 0.15 : 0) + sharp * 0.1)
    : 0.25;

  return {
    id: 'skin_smoothing',
    score,
    confidence,
    note:
      score >= 0.35
        ? 'низкий локальный контраст кожи при сохранении резких краёв — возможна ретушь/сглаживание'
        : 'локальный контраст кожи без признаков сглаживания',
  };
};
